import { FC, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { UpdateJobLineItemRequestData } from '@payaca/store/jobContent/jobContentTypes';
import { JobLineItem, JobLineItemBase } from '@payaca/types/jobContentTypes';

import { SortableList } from '@payaca/dnd';
import JobLineItemControl from '../jobLineItemControl/JobLineItemControl';
import AddJobLineItemControl from './AddJobLineItemControl';

import { getJobLineItem, getJobLineItemGroup } from '@/utils/stateAccessors';
import { by } from '@payaca/utilities/sortable';

import { useSelector } from '@/api/state';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';

import { getSortedRequiredMultipleOptionalItems } from '@payaca/helpers/jobLineItemHelper';

type Props = {
  jobLineItemGroupId: number;
  jobIsInvoice: boolean;
  handleOpenCreateJobLineItemModal: (
    jobLineItemId: number,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  handleCreateJobLineItemAndOpenCreateJobLineItemModal: (
    lineItemId: number | null,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  handleOpenAdvancedSearchLineItemModal: (searchTerm?: string) => void;
  disabledItemIds: number[];
};

const JobLineItemsControl: FC<Props> = ({
  jobLineItemGroupId,
  jobIsInvoice,
  handleOpenCreateJobLineItemModal,
  handleCreateJobLineItemAndOpenCreateJobLineItemModal,
  handleOpenAdvancedSearchLineItemModal,
  disabledItemIds,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const jobLineItemGroup = useSelector((state) =>
    getJobLineItemGroup(state, jobLineItemGroupId)
  );

  const jobLineItemIds = jobLineItemGroup?.jobLineItemIds ?? [];

  const sortedJobLineItems = useSelector((state) => {
    const jlis = jobLineItemIds
      .map((id: number) => getJobLineItem(state, id))
      .filter(Boolean) as Array<JobLineItem>;
    return jlis.sort(by('positionIndex'));
  });

  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);

  const [pendingSortedJobLineItems, setPendingSortedJobLineItems] = useState<
    JobLineItem[] | null
  >(null);

  const onDragEnd = (
    activeItem: any,
    _destinationIndex: number,
    updatedItems: JobLineItem[]
  ) => {
    if (!activeItem || !jobLineItemGroup?.jobContentId) {
      return false;
    }

    // Items are always ordered required → optional → multi-choice and may only
    // be reordered within their own section. Re-sorting the dragged list back
    // into section order clamps any cross-section drag to the item's section
    // boundary; the dragged item's index in that order is the position we save.
    const { required, optional, multiple } =
      getSortedRequiredMultipleOptionalItems(updatedItems);
    const orderedUpdatedItems = [...required, ...optional, ...multiple];

    const newPositionIndex = orderedUpdatedItems.findIndex(
      (item) => item.id === activeItem.id
    );

    setIsUpdatingPositions(true);
    setPendingSortedJobLineItems(orderedUpdatedItems);

    dispatch(
      jobContentActions.requestUpdateJobLineItem(
        {
          ...activeItem,
          positionIndex: newPositionIndex,
          updateLineItem: false,
        } as UpdateJobLineItemRequestData,
        () => {
          // get updated group
          dispatch(
            jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
              jobLineItemGroup.jobContentId,
              () => {
                setIsUpdatingPositions(false);
                // clear pending sorted job line items, so we can now use the saved fetched job line items (or revert if failed to save)
                setPendingSortedJobLineItems(null);
              }
            )
          );
        }
      )
    );

    // We drive optimistic ordering via `pendingSortedJobLineItems` (the
    // section-clamped order), so tell SortableList not to also commit its own
    // un-clamped order.
    return false;
  };

  return (
    <div className="job-line-items-control">
      <div className="job-line-item-group-controls-container">
        <SortableList
          id={`job-line-items-control-${jobLineItemGroup?.id}`}
          items={pendingSortedJobLineItems || sortedJobLineItems}
          onDragEnd={onDragEnd}
          isDisabled={isUpdatingPositions}
          className="grid grid-cols-1 gap-2"
          renderItem={(item) => (
            <JobLineItemControl
              jobLineItemId={item.jobLineItemId}
              key={`job-line-item-control-${item.jobLineItemId}`}
              handleOpenCreateJobLineItemModal={
                handleOpenCreateJobLineItemModal
              }
              dragHandleId={item.id}
            />
          )}
        />
      </div>

      <div className="mt-2">
        <AddJobLineItemControl
          handleOpenAdvancedSearchLineItemModal={
            handleOpenAdvancedSearchLineItemModal
          }
          handleCreateJobLineItemAndOpenCreateJobLineItemModal={
            handleCreateJobLineItemAndOpenCreateJobLineItemModal
          }
          disabledItemIds={disabledItemIds}
        />
      </div>
    </div>
  );
};
export default JobLineItemsControl;
